import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useController, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { InitiativeWriteInput, SalesCycle } from '@complan/contracts';
import { formatDateRange } from '@/shared/format/date-format';
import { Field, TextInput, TextArea, ControlledSelect, CheckboxGroup, ConditionalBlock } from './fields';
import type { ControlledOption, IntakeOptionSets } from './intake-options';
import { ValidationSummary } from './ValidationSummary';
import type { SummaryFinding } from './validation-model';
import type { MissingRequiredField } from './required-fields';
import type { WizardStepId } from './wizard-steps';
import { useSalesCycleDerivation } from './use-sales-cycle-derivation';
import { dateRangeOfCycles, useSelectableSalesCycles } from './use-selectable-sales-cycles';
import { DateRangeField } from '@/shared/components/DateRangeField';
import type { DateRange } from '@/shared/components/InlineDateRangePicker';

/**
 * The non-Field-Force intake steps (R5.1). Each reads the RHF context by name;
 * per-field subscriptions via {@link useController} keep a keystroke in one
 * field from re-rendering the others (UI spec §6, RHF 7).
 *
 * All copy is resolved from the catalogue at the call site (R18.9); the field
 * primitives themselves carry none.
 */

type FormValues = InitiativeWriteInput;

/** A checkbox-group step over a string-id array field (channels, categories, etc.). */
export function IdArrayCheckboxField({
  name,
  legend,
  options,
  selectAll = false,
}: {
  readonly name:
    | 'channelIds'
    | 'audienceIds'
    | 'customerGroupIds'
    | 'categoryIds'
    | 'initiativeTypeIds'
    | 'cscAttributeIds';
  readonly legend: string;
  readonly options: readonly ControlledOption[];
  /** Shows a "Select all" checkbox above the group that ticks or clears every shown option. */
  readonly selectAll?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { field } = useController<FormValues>({ name });
  const selected = (field.value as string[] | undefined) ?? [];
  const group = (
    <CheckboxGroup
      legend={legend}
      options={options}
      selected={selected}
      inactiveMarker={t('intake.inactiveMarker')}
      onToggle={(id, checked) => {
        const next = checked ? [...selected, id] : selected.filter((value) => value !== id);
        field.onChange(next);
      }}
    />
  );
  if (!selectAll || options.length === 0) return group;

  // "Shown" = the options on screen (active ones plus any retained-inactive one).
  // Clearing removes only those, and ticking keeps any other value already held.
  const shownIds = options.map((option) => option.id);
  const selectedSet = new Set(selected);
  const shownSelected = shownIds.filter((id) => selectedSet.has(id)).length;
  const allSelected = shownSelected === shownIds.length;
  return (
    <>
      <label className="mb-0.5 flex w-fit cursor-pointer items-center gap-2 px-1.5 py-[3px] text-[12px] font-semibold text-txt">
        <input
          type="checkbox"
          checked={allSelected}
          ref={(el) => {
            if (el) el.indeterminate = shownSelected > 0 && !allSelected;
          }}
          onChange={(event) => {
            const shown = new Set(shownIds);
            const others = selected.filter((id) => !shown.has(id));
            field.onChange(event.target.checked ? [...others, ...shownIds] : others);
          }}
        />
        <span>{t('intake.selectAll')}</span>
      </label>
      {group}
    </>
  );
}

/** Step 1 — Basics: title, details, owner, backup owner (R5.1, R5.6). */
export function BasicsStep({ options }: { readonly options: IntakeOptionSets }): React.JSX.Element {
  const { t } = useTranslation();
  const { register, control } = useFormContext<FormValues>();
  const owner = useController<FormValues>({ name: 'ownerUserId', control });
  const backup = useController<FormValues>({ name: 'backupOwnerUserId', control });
  return (
    <section>
      <h2 className="mb-3 text-[13px] font-semibold text-txt">{t('intake.basics.heading')}</h2>
      <Field label={t('intake.basics.titleLabel')} htmlFor="intake-title" required requiredMark={t('intake.requiredMark')}>
        <TextInput id="intake-title" placeholder={t('intake.basics.titlePlaceholder')} {...register('title')} />
      </Field>
      <Field label={t('intake.basics.detailsLabel')} htmlFor="intake-details">
        <TextArea id="intake-details" placeholder={t('intake.basics.detailsPlaceholder')} {...register('details')} />
      </Field>
      <Field label={t('intake.basics.ownerLabel')} htmlFor="intake-owner" required requiredMark={t('intake.requiredMark')}>
        <ControlledSelect
          id="intake-owner"
          options={options.owners}
          placeholder={t('intake.basics.ownerPlaceholder')}
          inactiveMarker={t('intake.inactiveMarker')}
          value={(owner.field.value as string | undefined) ?? ''}
          onChange={(event) => owner.field.onChange(event.target.value)}
        />
      </Field>
      <Field label={t('intake.basics.backupOwnerLabel')} htmlFor="intake-backup-owner">
        <ControlledSelect
          id="intake-backup-owner"
          options={options.owners}
          placeholder={t('intake.basics.backupOwnerPlaceholder')}
          inactiveMarker={t('intake.inactiveMarker')}
          value={(backup.field.value as string | undefined) ?? ''}
          onChange={(event) => backup.field.onChange(event.target.value || null)}
        />
      </Field>
    </section>
  );
}

/**
 * Wrapper for the compact `DateRangeField` in the Timing step. The trigger
 * shows the formatted start–end text; clicking it opens the existing calendar
 * picker (`InlineDateRangePicker`, unchanged) as a popover.
 */
function TimingCalendarPicker({
  startDate,
  endDate,
  onChange,
}: {
  readonly startDate: string | undefined;
  readonly endDate: string | undefined;
  readonly onChange: (range: DateRange | null) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const value: DateRange | null = useMemo(() => {
    if (startDate && endDate) {
      return { startDate, endDate };
    }
    return null;
  }, [startDate, endDate]);

  return (
    <DateRangeField
      value={value}
      onChange={onChange}
      label={t('intake.timing.dateRangeLabel')}
      minDate="2020-01-01" // Reasonable min date for planning
      maxDate="2035-12-31" // Reasonable max date for planning
    />
  );
}

/**
 * Step 2 — Timing and Sales Cycle (R5.1, R7.1-R7.3).
 *
 * Uses an inline calendar picker instead of separate date input fields for better UX.
 * Users can click directly on the calendar to select start and end dates.
 * `startDate`/`endDate` are the only fields this step registers directly; the
 * Sales_Cycle assignment is derived from them rather than entered. On every
 * settled (debounced) change to a well-formed range, {@link useSalesCycleDerivation}
 * asks `POST /api/sales-cycle-derivation` for the intersecting active cycles, and
 * this step writes the result into `salesCycleAssignment` — but **only** while the
 * assignment source is still `derived`. R7.7 requires a manual override to survive
 * a later date change unchanged; writing here regardless of source would silently
 * replace a Commercial_Planning_User's override the moment they touched a date
 * field again, which is exactly the rule R7.7 exists to prevent. (Foundation has
 * no manual-override control in this wizard yet — the write path and the
 * Detail_Panel's override display exist, but nothing here can *set* `source:
 * 'manual'` — so in practice this guard is inert today and becomes load-bearing
 * once that control ships.)
 *
 * The proposal is shown as an `.cond`/`.alert` info block (UI spec §6.1's
 * "Sales_Cycle proposal alert"), naming the derived cycles or, when the range
 * intersects none, the R7.9 governance consequence (blocks every transition out
 * of Draft) so the user is not left guessing why a later submit is refused.
 */
export function TimingStep(): React.JSX.Element {
  const { t } = useTranslation();
  // No `register` here: the two dates are written through `setValue` by the
  // calendar and Sales_Cycle pickers below, not by a registered native input.
  const { control, setValue } = useFormContext<FormValues>();
  const [startDate, endDate, assignment] = useWatch({
    control,
    name: ['startDate', 'endDate', 'salesCycleAssignment'],
  });

  const derivation = useSalesCycleDerivation(
    startDate as string | undefined,
    endDate as string | undefined,
  );

  // `derivation.derivedSalesCycles` is a fresh array each resolution; the ids it
  // carries are what actually decides whether the effect below has new work, so
  // that stable, joined string is its dependency rather than the array identity.
  const derivedIdsKey = derivation.derivedSalesCycles.map((cycle) => cycle.id).join(',');

  // Write the derived set into the form once it resolves, but never while a
  // manual override is in force (R7.7) — see this function's doc comment.
  useEffect(() => {
    if (!derivation.hasResolved) return;
    if (assignment?.source === 'manual') return;
    setValue(
      'salesCycleAssignment',
      {
        source: 'derived',
        salesCycleIds: derivedIdsKey === '' ? [] : derivedIdsKey.split(','),
      },
      { shouldDirty: true },
    );
  }, [derivation.hasResolved, derivedIdsKey, assignment?.source, setValue]);

  const isManualOverride = assignment?.source === 'manual';

  return (
    <section>
      <h2 className="mb-3 text-[13px] font-semibold text-txt">{t('intake.timing.heading')}</h2>

      {/* Inline calendar date picker and Sales_Cycle picker side by side */}
      <div className="grid items-start gap-4 md:grid-cols-[auto_minmax(0,1fr)]">
        <TimingCalendarPicker
          startDate={startDate as string | undefined}
          endDate={endDate as string | undefined}
          onChange={(range) => {
            if (range) {
              setValue('startDate', range.startDate, { shouldDirty: true, shouldValidate: true });
              setValue('endDate', range.endDate, { shouldDirty: true, shouldValidate: true });
            } else {
              setValue('startDate', '', { shouldDirty: true, shouldValidate: true });
              setValue('endDate', '', { shouldDirty: true, shouldValidate: true });
            }
          }}
        />

        <SalesCyclePicker
          derivedSalesCycleIds={derivedIdsKey === '' ? [] : derivedIdsKey.split(',')}
          onPick={(range) => {
            setValue('startDate', range.startDate, { shouldDirty: true, shouldValidate: true });
            setValue('endDate', range.endDate, { shouldDirty: true, shouldValidate: true });
          }}
        />
      </div>

      {isManualOverride ? (
        <ConditionalBlock className="mt-2" role="note">
          <p className="text-[11.5px] text-txt">{t('intake.timing.manualOverrideNote')}</p>
        </ConditionalBlock>
      ) : (
        <SalesCycleProposalAlert derivation={derivation} />
      )}
    </section>
  );
}

/**
 * The Sales_Cycle picker beside the two date fields: choosing one or more cycles
 * fills the dates with the earliest selected start and the latest selected end.
 *
 * ## The picker sets dates; the dates still decide the assignment
 *
 * This control writes **only** `startDate`/`endDate`. It deliberately does not
 * write `salesCycleAssignment`, because R7.1 makes the assignment a function of
 * the Initiative's date range, and R7.3 stores that result with source `derived`.
 * Keeping one direction of causation (pick → dates → derive → assignment) is what
 * stops the picker and the derivation from disagreeing about the stored set.
 *
 * A visible consequence, surfaced rather than hidden: picking **non-adjacent**
 * cycles produces a range that necessarily spans the cycles between them, so the
 * derived assignment is wider than the selection. A single date range cannot skip
 * a middle cycle, so this is a property of the data model rather than of this
 * control - {@link SalesCycleProposalAlert} always states the set that will
 * actually be stored, and the hint below names the additions explicitly.
 *
 * ## What "selected" shows
 *
 * Until the user touches the picker it mirrors the derived set, so opening the
 * step for an existing Initiative shows the cycles it is actually assigned to.
 * Once touched it shows the user's own selection, so their intent is not
 * overwritten by the wider derived set the note explains.
 */
function SalesCyclePicker({
  derivedSalesCycleIds,
  onPick,
}: {
  readonly derivedSalesCycleIds: readonly string[];
  readonly onPick: (range: {
    readonly startDate: SalesCycle['startDate'];
    readonly endDate: SalesCycle['endDate'];
  }) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { data: cycles, isLoading, isError } = useSelectableSalesCycles();
  // `null` means "not touched yet, mirror the derived set" — see the doc comment.
  const [picked, setPicked] = useState<readonly string[] | null>(null);

  const available = useMemo(
    () =>
      [...(cycles ?? [])].sort(
        (a, b) => a.planningYear - b.planningYear || (a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0),
      ),
    [cycles],
  );

  const options: readonly ControlledOption[] = useMemo(
    () =>
      available.map((cycle) => ({
        id: cycle.id,
        label: t('intake.timing.cycleOptionLabel', {
          name: cycle.name,
          year: cycle.planningYear,
          range: formatDateRange(cycle.startDate, cycle.endDate),
        }),
        isInactive: false,
      })),
    [available, t],
  );

  const selected = picked ?? derivedSalesCycleIds;

  const toggle = (id: string, checked: boolean): void => {
    const next = checked ? [...selected, id] : selected.filter((value) => value !== id);
    setPicked(next);
    const range = dateRangeOfCycles(available.filter((cycle) => next.includes(cycle.id)));
    // An empty selection leaves the entered dates alone rather than clearing
    // them: unchecking the last cycle is not a request to discard the timing.
    if (range !== null) {
      onPick(range);
    }
  };

  // Cycles the derivation added on top of an explicit selection (the
  // non-adjacent case described in this component's doc comment).
  const addedByDerivation = picked === null ? [] : derivedSalesCycleIds.filter((id) => !picked.includes(id));
  const addedNames = available
    .filter((cycle) => addedByDerivation.includes(cycle.id))
    .map((cycle) => cycle.name);

  return (
    <Field label={t('intake.timing.cyclePickerLabel')} hint={t('intake.timing.cyclePickerHint')}>
      {isLoading ? (
        <p className="text-muted-foreground text-[11.5px]">{t('intake.timing.cyclesLoading')}</p>
      ) : isError ? (
        <p className="text-[11.5px] text-[var(--warn)]">{t('intake.timing.cyclesLoadError')}</p>
      ) : options.length === 0 ? (
        <p className="text-muted-foreground text-[11.5px]">{t('intake.timing.cyclesEmpty')}</p>
      ) : (
        <>
          <CheckboxGroup
            legend={t('intake.timing.cyclePickerLabel')}
            options={options}
            selected={selected}
            inactiveMarker={t('intake.inactiveMarker')}
            onToggle={toggle}
            columns={1}
          />
          {addedNames.length > 0 ? (
            <p className="mt-1 text-[11px] text-[var(--warn)]" role="status">
              {t('intake.timing.cyclesAddedByRange', { cycles: addedNames.join(', ') })}
            </p>
          ) : null}
        </>
      )}
    </Field>
  );
}

/**
 * The Sales_Cycle proposal alert (UI spec §6.1's "Intake wizard's Sales_Cycle
 * proposal alert", `.cond`/`.alert` conventions): states the derived cycles for
 * the entered dates, or — while a range is not yet resolvable, or resolves to no
 * active cycle (R7.9) — the corresponding info/governance message.
 */
function SalesCycleProposalAlert({
  derivation,
}: {
  readonly derivation: ReturnType<typeof useSalesCycleDerivation>;
}): React.JSX.Element {
  const { t } = useTranslation();

  if (!derivation.hasResolved) {
    return (
      <p className="text-muted-foreground text-[11.5px]" aria-live="polite">
        {derivation.isDeriving
          ? t('intake.timing.derivingNote')
          : t('intake.timing.salesCycleNote')}
      </p>
    );
  }

  if (derivation.derivedSalesCycles.length === 0) {
    return (
      <ConditionalBlock className="mt-2" role="alert">
        <p className="text-[11.5px] text-[var(--warn)]">{t('intake.timing.noSalesCycleWarning')}</p>
      </ConditionalBlock>
    );
  }

  return (
    <ConditionalBlock className="mt-2" role="status" aria-live="polite">
      <p className="text-[11.5px] text-txt">
        {t('intake.timing.derivedSalesCycleNote', {
          cycles: derivation.derivedSalesCycles.map((cycle) => cycle.name).join(', '),
        })}
      </p>
    </ConditionalBlock>
  );
}

/** Step 3 — Classification: platforms, categories, types, priority (R5.1). */
export function ClassificationStep({ options }: { readonly options: IntakeOptionSets }): React.JSX.Element {
  const { t } = useTranslation();
  const { control } = useFormContext<FormValues>();
  const priority = useController<FormValues>({ name: 'priorityId', control });
  const platforms = useController<FormValues>({ name: 'platforms', control });
  const selectedPlatforms = ((platforms.field.value as FormValues['platforms'] | undefined) ?? []).map(
    (link) => link.platformId,
  );
  return (
    <section>
      <h2 className="mb-3 text-[13px] font-semibold text-txt">{t('intake.classification.heading')}</h2>
      <Field label={t('intake.classification.platformLegend')} hint={t('intake.classification.platformNote')}>
        <CheckboxGroup
          legend={t('intake.classification.platformLegend')}
          options={options.platforms}
          selected={selectedPlatforms}
          inactiveMarker={t('intake.inactiveMarker')}
          onToggle={(id, checked) => {
            const current = (platforms.field.value as FormValues['platforms'] | undefined) ?? [];
            const next = checked
              ? [...current, { platformId: id, isPrimary: current.length === 0, splitPercent: null }]
              : current.filter((link) => link.platformId !== id);
            platforms.field.onChange(next);
          }}
        />
      </Field>
      <Field label={t('intake.classification.categoryLegend')}>
        <IdArrayCheckboxField name="categoryIds" legend={t('intake.classification.categoryLegend')} options={options.categories} />
      </Field>
      <Field label={t('intake.classification.initiativeTypeLegend')}>
        <IdArrayCheckboxField name="initiativeTypeIds" legend={t('intake.classification.initiativeTypeLegend')} options={options.initiativeTypes} />
      </Field>
      <Field label={t('intake.classification.priorityLabel')} htmlFor="intake-priority" required requiredMark={t('intake.requiredMark')}>
        <ControlledSelect
          id="intake-priority"
          options={options.priorities}
          placeholder={t('intake.classification.priorityPlaceholder')}
          inactiveMarker={t('intake.inactiveMarker')}
          value={(priority.field.value as string | undefined) ?? ''}
          onChange={(event) => priority.field.onChange(event.target.value)}
        />
      </Field>
    </section>
  );
}

/**
 * Step 4 — Audience and impacted channels (R5.1, R5.4). The named-customer /
 * partner-group block is revealed via the `.cond` treatment when the audience
 * selection includes KAM or IND (UI spec §6.1). Foundation has no audience-code
 * master data distinguishing KAM/IND on the wire, so the reveal keys off the
 * audience option whose id carries the `kam`/`ind` code — see the route note; the
 * customer-group block itself is always available but marked required by the
 * server rule when a KAM/IND audience is present.
 */
export function AudienceStep({
  options,
  showNamedCustomerBlock,
}: {
  readonly options: IntakeOptionSets;
  readonly showNamedCustomerBlock: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <section>
      <h2 className="mb-3 text-[13px] font-semibold text-txt">{t('intake.audience.heading')}</h2>
      <Field label={t('intake.audience.channelLegend')}>
        <IdArrayCheckboxField name="channelIds" legend={t('intake.audience.channelLegend')} options={options.channels} selectAll />
      </Field>
      <Field label={t('intake.audience.audienceLegend')}>
        <IdArrayCheckboxField name="audienceIds" legend={t('intake.audience.audienceLegend')} options={options.audiences} />
      </Field>
      {showNamedCustomerBlock ? (
        <ConditionalBlock className="mt-2">
          <h3 className="mb-1 text-[12px] font-semibold text-txt">{t('intake.audience.namedCustomerHeading')}</h3>
          <p className="text-muted-foreground mb-2 text-[11px]">{t('intake.audience.namedCustomerNote')}</p>
          <IdArrayCheckboxField
            name="customerGroupIds"
            legend={t('intake.audience.customerGroupLegend')}
            options={options.customerGroups}
          />
        </ConditionalBlock>
      ) : null}
    </section>
  );
}

/** Step 8 — Governance attributes: confidentiality (R5.1, R3.x). */
export function GovernanceStep(): React.JSX.Element {
  const { t } = useTranslation();
  const { control, register } = useFormContext<FormValues>();
  const confidential = useController<FormValues>({ name: 'isConfidential', control });
  const isConfidential = Boolean(confidential.field.value);
  return (
    <section>
      <h2 className="mb-3 text-[13px] font-semibold text-txt">{t('intake.governance.heading')}</h2>
      <Field label={t('intake.governance.confidentialLabel')}>
        <label className="flex items-center gap-2 text-[12px] text-txt">
          <input
            type="checkbox"
            checked={isConfidential}
            onChange={(event) => confidential.field.onChange(event.target.checked)}
          />
          <span>{t('common.yes')}</span>
        </label>
      </Field>
      {isConfidential ? (
        <ConditionalBlock>
          <Field label={t('intake.governance.confidentialReasonLabel')} htmlFor="intake-conf-reason">
            <TextArea
              id="intake-conf-reason"
              placeholder={t('intake.governance.confidentialReasonPlaceholder')}
              {...register('confidentialityReason')}
            />
          </Field>
        </ConditionalBlock>
      ) : null}
    </section>
  );
}

/**
 * Step 9 — Validation summary (task 23.2). Renders the four-tier summary and the
 * submit gate; the wizard supplies the merged findings, the acknowledgement set
 * and the action handlers. Task 23.1 reserved this seam.
 */
export function ValidationStep({
  findings,
  unacknowledgedWarningRuleIds,
  onAcknowledgeWarning,
  onSaveDraft,
  onSubmit,
  isRevalidating,
  missingFields,
  onNavigateToStep,
}: {
  readonly findings: readonly SummaryFinding[];
  readonly unacknowledgedWarningRuleIds: readonly string[];
  readonly onAcknowledgeWarning: (ruleId: string) => void;
  readonly onSaveDraft: () => void;
  readonly onSubmit: () => void;
  readonly isRevalidating: boolean;
  /** Required inputs still empty across the whole intake (advisory + jump target). */
  readonly missingFields: readonly MissingRequiredField[];
  readonly onNavigateToStep: (step: WizardStepId) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <section>
      <h2 className="mb-3 text-[13px] font-semibold text-txt">{t('intake.validationStep.heading')}</h2>
      <ValidationSummary
        findings={findings}
        unacknowledgedWarningRuleIds={unacknowledgedWarningRuleIds}
        onAcknowledgeWarning={onAcknowledgeWarning}
        onSaveDraft={onSaveDraft}
        onSubmit={onSubmit}
        isRevalidating={isRevalidating}
        missingFields={missingFields}
        onNavigateToStep={onNavigateToStep}
      />
    </section>
  );
}
